import { useState, useEffect, Dispatch, SetStateAction, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from '../../hooks/use-toast.js';
import { apiRequest } from '../../lib/queryClient.js';
import { useAuth } from '../../hooks/use-auth.js';
import { cacheManager } from '../../utils/cacheUtils';
import { createChapterMetadata } from '../../utils/hasChaptersCanonical';

import { 
    ProjectState, Chapter, ChapterDetailEdits, LoadingStates, BookMetadata, WorldBuildingData, 
    ChapterVitals, MainView, FrontMatter, BackMatter, PublishingEssentials,
    UpdateBackMatterListItemArgsTuple, AuthorDetails, ChapterVersion, getActiveVersion, getTargetWordCount
} from '../types';
import { Project, BlueprintState } from '../types';

import { 
    initialAuthorDetails, 
    initialFrontMatter, 
    initialBackMatter, 
    initialPublishingEssentials,
    initialChapterVitals,
    initialBookMetadata,
    initialWorldBuildingData,
    LOCAL_STORAGE_ACTIVE_PROJECT_KEY
} from '../constants.js';
import { loadInitialProjectState, normalizeProjectState } from '../utils/projectStateUtils.js';
import { useChapterState } from './useChapterState.js';
import { deriveChaptersState } from '../../utils/chapterStateUtils.js';

export interface CoreState {
    projectState: ProjectState | null;
    setProjectState: Dispatch<SetStateAction<ProjectState | null>>;
    isLoading: LoadingStates;
    creditBalance: number;
    requiredCredits: number;
    activeChapter: Chapter | null;
    editor: any; // Placeholder for editor state
    isInitialBlueprintReady: boolean;
    dbProject: Project | null; // Database project for accessing bookType
    confirmOverallDiscardChanges: (message?: string) => boolean; // Changed to return boolean
    haveDetailsOrWorldChangedSinceChapterGen: () => boolean;
    detailsOrWorldDirtyButOK: boolean;
    apiKey: string | null;
    setApiKey: Dispatch<SetStateAction<string | null>>;
    openaiApiKey: string | null;
    setOpenaiApiKey: Dispatch<SetStateAction<string | null>>;
    deepseekApiKey: string | null;
    setDeepseekApiKey: Dispatch<SetStateAction<string | null>>;
    setLoadingState: (key: string, value: boolean) => void;
    setError: Dispatch<SetStateAction<string | null>>;
    clearError: () => void;
    getCoreProjectContextForAI: () => any;
    mainView: MainView;
    setMainView: (view: MainView) => void;
    setChaptersReady: Dispatch<SetStateAction<boolean>>;
    setIsInitialBlueprintReady: Dispatch<SetStateAction<boolean>>; // Added
    determineInitialViewBasedOnState: (projectState: ProjectState, hasInitialBlueprint: boolean) => MainView;
    activeVersion: ChapterVersion | null;

    // Project state handlers
    handleProjectStateFieldChange: (field: keyof ProjectState, value: string) => void;
    handleTargetWordCountChange: (value: string) => void;
    handleTargetPageCountChange: (value: string) => void; // Added
    handleBookMetadataChange: (field: keyof BookMetadata, value: string) => void;
    handleBlueprintChange: (section: 'bookMetadata' | 'worldBuildingSuggestions', field: keyof WorldBuildingData | keyof BookMetadata | "all", value: any) => void;
    handleAuthorDetailsChange: (field: keyof AuthorDetails, value: string | null) => void;
    handleFrontMatterChange: <K extends keyof FrontMatter>(field: K, value: FrontMatter[K]) => void;
    handleBackMatterChange: <K extends keyof BackMatter>(field: K, value: BackMatter[K]) => void;
    handlePublishingEssentialsChange: <K extends keyof PublishingEssentials>(field: K, value: PublishingEssentials[K]) => void;
    handleChaptersChange: (newChapters: Chapter[]) => void;

    // Chapter management props
    handleSelectChapter: (chapterId: string) => void;
    openSplitChapterModal: (chapter: Chapter) => void;
    onToggleChapterForMerge: (chapterId: string) => void;
    isChapterSelectionDisabledForMerge: (chapterId: string) => boolean;
    mergeableSequenceInfo: { isMergeable: boolean; selectedChapters: Chapter[]; firstIndexInProject: number; lastIndexInProject: number; };
    onOpenMergeModal: () => void;
    activeChapterDetailEdits: Partial<ChapterDetailEdits> | null;
    setActiveChapterDetailEdits: Dispatch<SetStateAction<Partial<ChapterDetailEdits> | null>>;
    revisionInput: string;
    revisionWordCount: string;
    handleSaveChapterDetails: () => void;
    handleGenerateChapterPreview: () => void; 
    handleGenerateFullTextFromPreview: () => void; 
    handleGenerateFullTextForAllPreviewedChapters: () => void;
    handleGenerateShortPreviewForAllChapters: () => void; 
    setRevisionInput: Dispatch<SetStateAction<string>>;
    setRevisionWordCount: Dispatch<SetStateAction<string>>;
    handleReviseText: () => void;
    navigateVersion: (direction: 'prev' | 'next') => void;
    setChapterStatus: (status: Chapter['status']) => void;
    chapterPreviewPercentage: number; 
    onChapterPreviewPercentageChange: (percentage: number) => void; 

    // Book structure props
    onAddBackMatterListItem: (listField: 'appendices' | 'glossary' | 'bibliography') => void;
    onDeleteBackMatterListItem: (listField: 'appendices' | 'glossary' | 'bibliography', id: string) => void;
    onUpdateBackMatterListItem: (...args: UpdateBackMatterListItemArgsTuple) => void;
    
    // Additional handlers
    handleGenerateInitialBlueprint: () => void;
    handleGenerateChapterOutlines: () => void;
    handleRecalculateChapterWordCounts: () => void;
    error: string | null;
    
    // Chapter state functions (from useChapterState)
    handleDetailInputChange: (field: keyof Omit<ChapterDetailEdits, 'vitals'>, value: string) => void;
    handleVitalsInputChange: (vitalField: keyof ChapterVitals, value: string) => void;
    areChapterDetailsCurrentlyUnsaved: () => boolean;
}

export const useCoreState = (
    initialProjectState: ProjectState | null,
    setInitialProjectState: Dispatch<SetStateAction<ProjectState | null>>,
    editor: any, // Accept editor as an argument
    dbProject: Project | null, // Database project for accessing bookType
    blueprintState?: BlueprintState // Blueprint state for change tracking
): CoreState => {
    const { user } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [projectState, setProjectState] = useState<ProjectState | null>(initialProjectState);
    const [isLoading, setIsLoading] = useState<LoadingStates>({});
    const [activeChapter, setActiveChapter] = useState<Chapter | null>(null);
    const [isInitialBlueprintReady, setIsInitialBlueprintReady] = useState(false);
    const [mainView, setMainView] = useState<MainView>('ideaHub'); // Internal state for mainView
    // Use consolidated chapter state hook
    const chapterState = useChapterState(activeChapter || undefined, mainView, isInitialBlueprintReady);
    const { 
        activeChapterDetailEdits, 
        setActiveChapterDetailEdits, 
        revisionInput, 
        setRevisionInput, 
        revisionWordCount, 
        setRevisionWordCount,
        handleDetailInputChange: chapterHandleDetailInputChange,
        handleVitalsInputChange: chapterHandleVitalsInputChange,
        areChapterDetailsCurrentlyUnsaved: chapterAreChapterDetailsCurrentlyUnsaved
    } = chapterState;
    const [selectedChapterIdsForMerge, setSelectedChapterIdsForMerge] = useState<string[]>([]);
    const [mergeableSequenceInfo, setMergeableSequenceInfo] = useState({
        isMergeable: false,
        selectedChapters: [] as Chapter[],
        firstIndexInProject: 0,
        lastIndexInProject: 0
    });
    // areChapterDetailsCurrentlyUnsaved now comes from useChapterState
    const [chapterPreviewPercentage, setChapterPreviewPercentage] = useState(30);
    const { apiKey, setApiKey, openaiApiKey, setOpenaiApiKey, deepseekApiKey, setDeepseekApiKey } = useAuth();
    const [error, setError] = useState<string | null>(null);
    const [chaptersReady, setChaptersReady] = useState(false);
    const [preventSyncOverride, setPreventSyncOverride] = useState(false); // Prevent sync from overriding user actions

    // Sync initial project state - but preserve chapters if they exist and don't override user actions
    useEffect(() => {
        if (!initialProjectState) return;
        if (preventSyncOverride) {
            return;
        }

        setProjectState(prev => {
            if (prev && prev.chapters && prev.chapters.length > 0) {
                const merged = {
                    ...initialProjectState,
                    // keep the locally edited chapters
                    chapters: prev.chapters,
                    // 🔒 always keep the local selection if chapters exist
                    activeChapterId: prev.activeChapterId ?? initialProjectState.activeChapterId ?? null,
                    bookMetadataSnapshotForChapters: prev.bookMetadataSnapshotForChapters,
                    blueprintSnapshotForChapters: prev.blueprintSnapshotForChapters,
                };
                return normalizeProjectState(merged);
            }
            return normalizeProjectState(initialProjectState);
        });

        setIsInitialBlueprintReady(initialProjectState.hasInitialBlueprint);
    }, [initialProjectState]); // ⬅ removed preventSyncOverride from deps

    // Sync isInitialBlueprintReady when projectState.hasInitialBlueprint changes
    useEffect(() => {
        if (projectState && projectState.hasInitialBlueprint !== undefined) {
            setIsInitialBlueprintReady(projectState.hasInitialBlueprint);
        }
    }, [projectState?.hasInitialBlueprint]);

    // Sync activeChapter based on projectState.activeChapterId
    useEffect(() => {
        if (!projectState) {
            setActiveChapter(null);
            return;
        }
        const { activeChapterId, chapters } = projectState;
        const chap = activeChapterId && chapters ? chapters.find((c: Chapter) => c.id === activeChapterId) : null;
        setActiveChapter(chap || null);
    }, [projectState?.activeChapterId, projectState?.chapters]);

    // Fetch user's credit balance
    const { data: creditData } = useQuery<{ balance: string | number } | undefined>({
        queryKey: ["/api/credits/stats"],
        enabled: !!user,
    });
    const creditBalance = typeof creditData?.balance === 'string' ? parseInt(creditData.balance) : (creditData?.balance || 0);
    const requiredCredits = 5; // Matches the API implementation

    // Derived state for activeVersion
    const activeVersion = getActiveVersion(activeChapter);

    // Helper to set loading state for specific keys
    const setLoadingState = useCallback((key: string, value: boolean) => {
        setIsLoading((prev: LoadingStates) => ({ ...prev, [key]: value }));
    }, []);

    const clearError = useCallback(() => setError(null), []);

    // Chapter outline generation mutation
    const generateChapterOutlinesMutation = useMutation({
        mutationFn: async (payload: any) => {
            const response = await apiRequest('/api/llm/chapters-outline', 'POST', payload);
            return response.json();
        },
        onSuccess: async (data, variables) => {
            // CRITICAL FIX: data.data is the chapters array directly, not data.data.chapters
            const chapters = data.data;

            // Update project state with new chapters
            setProjectState(prev => {
                if (!prev) return null;
                const updatedState = {
                    ...prev,
                    chapters: chapters || [],
                    isUnsaved: false, // Mark as saved since API call succeeded
                    // Clear any previous active chapter selection
                    activeChapterId: null,
                    // Reset merge selections
                    selectedChapterIdsForMerge: []
                };
                return updatedState;
            });

            // 🧹 CACHE INVALIDATION: Handle cache invalidation instructions from server
            if (data.cacheInvalidation) {

                // Clear stale localStorage caches as instructed by server
                data.cacheInvalidation.cacheKeys.forEach((cacheKey: string) => {
                    localStorage.removeItem(cacheKey);
                });

                // Also clear cacheManager caches for this project
                cacheManager.remove('project-summary', data.cacheInvalidation.projectUuid);
                cacheManager.remove('chapters', data.cacheInvalidation.projectUuid);

            }

            // Update cache with chapters and progress data
            if (projectState?.projectId) {
                cacheManager.updateProjectWithChapters(projectState.projectId, chapters || [], data.progress);

                // Invalidate queries to refresh project data
                queryClient.invalidateQueries({ queryKey: ["story-project", projectState.projectId] });
                queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });

                // 🎯 RACE CONDITION FIX: Use progress data included in chapters-outline response
                // The server already includes updated progress in the response, so use it directly
                const updatedProgress = data.progress || null;

                // 🔧 CANONICAL: Create comprehensive project summary with consistent metadata
                const currentTimestamp = new Date().toISOString();
                const canonicalChapterMetadata = createChapterMetadata(chapters, {
                    projectTitle: projectState.bookMetadata?.title || 'Untitled',
                    projectId: projectState.projectId,
                    projectUuid: (projectState as any).projectUuid
                });

                const completeProjectSummary = {
                    ...canonicalChapterMetadata,
                    chaptersLastUpdate: currentTimestamp,
                    updatedAt: currentTimestamp,
                    // Essential project identification for deriveChaptersState
                    projectTitle: projectState.bookMetadata?.title || 'Untitled',
                    projectId: projectState.projectId,
                    projectUuid: (projectState as any).projectUuid,
                    // Include the fresh progress data from the LLM response
                    progress: {
                        ...updatedProgress,
                        // Ensure progress chapters aligns with actual chapter count
                        chapters: canonicalChapterMetadata.chaptersOutline,
                        chaptersOutline: canonicalChapterMetadata.chaptersOutline
                    }
                };

                // Update React Query cache with complete data including progress
                queryClient.setQueryData(['story-project-summary', projectState.projectId], (oldSummary: any) => {
                    const updatedSummary = {
                        ...oldSummary,
                        ...completeProjectSummary,
                        // Preserve essential identifiers
                        id: oldSummary?.id || projectState.projectId,
                        uuid: oldSummary?.uuid || (projectState as any).projectUuid,
                        title: oldSummary?.title || projectState.bookMetadata?.title || 'Untitled'
                    };
                    console.log('⚡ Enhanced React Query cache update with chapter metadata:', {
                        hasChapters: updatedSummary.hasChapters,
                        chaptersCount: updatedSummary.chaptersCount,
                        chaptersOutline: updatedSummary.chaptersOutline,
                        progressOutline: updatedSummary.progress?.chaptersOutline || 0,
                        overall: updatedSummary.progress?.overall || 0,
                        timestamp: updatedSummary.updatedAt,
                        projectTitle: updatedSummary.projectTitle
                    });
                    return updatedSummary;
                });

                // Update localStorage cache with complete data
                cacheManager.set('project-summary', projectState.projectId, completeProjectSummary, 5 * 60 * 1000);

                // 🚀 CANONICAL: Update blueprint cache with chapter metadata for deriveChaptersState compatibility
                const existingBlueprint = cacheManager.getBlueprint(projectState.projectId);
                if (existingBlueprint) {
                    const enhancedBlueprint = {
                        ...existingBlueprint,
                        ...canonicalChapterMetadata,
                        chaptersLastUpdate: currentTimestamp,
                        updatedAt: currentTimestamp,
                        projectTitle: projectState.bookMetadata?.title || 'Untitled',
                        // Ensure blueprintData also has canonical metadata for hydration
                        blueprintData: {
                            ...existingBlueprint.blueprintData,
                            ...canonicalChapterMetadata,
                            updatedAt: currentTimestamp,
                            chaptersLastUpdate: currentTimestamp
                        }
                    };

                    cacheManager.setBlueprint(projectState.projectId, enhancedBlueprint);
                }

                // ⚠️ WARN: Log if progress data is missing (should not happen in normal operation)
                if (!updatedProgress) {
                    console.warn('⚠️ No progress data in chapters-outline response - this should not happen', {
                        responseKeys: Object.keys(data),
                        hasProgressField: 'progress' in data,
                        progressValue: data.progress
                    });
                }

                // Force invalidation to trigger re-fetch in components
                queryClient.invalidateQueries({ queryKey: ['story-project-summary', projectState.projectId] });
                queryClient.invalidateQueries({ queryKey: ['project-summary', projectState.projectId] });

                console.log('✅ ENHANCED CACHE WRITE-THROUGH COMPLETE:', {
                    chaptersCreated: chapters?.length || 0,
                    cacheSourcesUpdated: ['cacheManager', 'reactQuery', 'localStorage', 'blueprint'],
                    timestamp: currentTimestamp,
                    deriveChaptersStateIntegrated: true,
                    noDowngradeProtection: true
                });
            }

            // Force UI refresh by updating chapters ready state
            setChaptersReady(true);

            // Navigate to chapters view to show the generated chapters
            setMainView('chaptersBuilding');

            // Force a re-render by triggering a small state update
            setTimeout(() => {
                // This helps ensure the UI properly reflects the new chapters
                setProjectState(prev => prev ? { ...prev } : null);
            }, 100);

            toast({
                title: "Success",
                description: `Generated ${chapters?.length || 0} chapter outlines successfully!`
            });
        },
        onError: (error: any) => {
            setError(`Chapter outline generation failed: ${error.message}`);
            toast({
                title: "Error",
                description: `Failed to generate chapter outlines: ${error.message}`,
                variant: "destructive"
            });
        },
        onSettled: () => {
            setLoadingState('chaptersGen', false);
        }
    });

    const getCoreProjectContextForAI = useCallback(() => {
        if (!projectState) return {};
        return {
            outlineIdea: projectState.outlineIdea,
            bookMetadata: projectState.bookMetadata,
            worldBuildingData: projectState.blueprint?.worldBuildingSuggestions,
            authorDetails: projectState.authorDetails,
            frontMatter: projectState.frontMatter,
            backMatter: projectState.backMatter,
            publishingEssentials: projectState.publishingEssentials,
            chapters: projectState.chapters.map((ch: Chapter) => ({
                id: ch.id,
                title: ch.title,
                summary: ch.summary,
                geography: ch.geography,
                charactersInvolved: ch.charactersInvolved,
                characterActions: ch.characterActions,
                soulOfChapter: ch.soulOfChapter,
                expectedWordCount: ch.expectedWordCount,
                vitals: ch.vitals,
            })),
            activeChapterId: projectState.activeChapterId,
            currentProjectName: projectState.currentProjectName,
            isUnsaved: projectState.isUnsaved,
            hasInitialBlueprint: projectState.hasInitialBlueprint,
            chapterPreviewPercentage: projectState.chapterPreviewPercentage,
            exportChapterContentPreference: projectState.exportChapterContentPreference,
        };
    }, [projectState]);

    const determineInitialViewBasedOnState = useCallback((projectState: ProjectState, hasInitialBlueprint: boolean): MainView => {
        if (!projectState) return 'ideaHub';
        if (hasInitialBlueprint) {
            const chapters = projectState.chapters ?? [];
            if (chapters.length === 0) {
                return 'bookDetails'; // Show Project Overview first when blueprint is fresh
            }
            return 'chaptersBuilding'; // Default to chapters if chapters already exist
        }
        return 'ideaHub';
    }, []);

    // Project state handlers
    const handleProjectStateFieldChange = (field: keyof ProjectState, value: string) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                [field]: value,
                isUnsaved: true,
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handleTargetWordCountChange = (value: string) => {
        // Allow empty string to enable proper editing, fallback to 0 for calculations
        const numValue = value === '' ? undefined : (parseInt(value, 10) || 0);
        // Update both the database project and project state
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                // Store target word count in the project state for local use
                targetWordCount: numValue,
                isUnsaved: true, // Mark as unsaved to trigger save
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
        
        // Note: The actual database update will happen when handleSaveProject is called
        // The dbProject.targetWordCount will be updated through the API
    };

    const handleTargetPageCountChange = (value: string) => {
        const numValue = value ? parseInt(value, 10) : undefined;
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                bookMetadata: {
                    ...prev.bookMetadata,
                    targetPageCount: numValue,
                },
                isUnsaved: true,
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handleBookMetadataChange = (field: keyof BookMetadata, value: string) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                bookMetadata: {
                    ...prev.bookMetadata,
                    [field]: value,
                },
                isUnsaved: true,
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handleBlueprintChange = (section: 'bookMetadata' | 'worldBuildingSuggestions', field: keyof WorldBuildingData | keyof BookMetadata | "all", value: any) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;

            // NEW SEGREGATION: Handle bookMetadata and worldBuildingSuggestions separately
            if (section === 'bookMetadata') {
                return {
                    ...prev,
                    bookMetadata: field === "all" ?
                        value : {
                            ...(prev.bookMetadata || {}),
                            [field]: value,
                        },
                    isUnsaved: true,
                };
            } else {
                // worldBuildingSuggestions stored in blueprint
                return {
                    ...prev,
                    blueprint: {
                        ...prev.blueprint,
                        worldBuildingSuggestions: field === "all" ?
                            value : {
                                ...(prev.blueprint?.worldBuildingSuggestions || {}),
                                [field]: value,
                            },
                    },
                    isUnsaved: true,
                };
            }
        });

        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handleAuthorDetailsChange = (field: keyof AuthorDetails, value: string | null) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                authorDetails: {
                    ...prev.authorDetails,
                    [field]: value,
                },
                isUnsaved: true,
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handleFrontMatterChange = <K extends keyof FrontMatter>(field: K, value: FrontMatter[K]) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                frontMatter: {
                    ...prev.frontMatter,
                    [field]: value,
                },
                isUnsaved: true,
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handleBackMatterChange = <K extends keyof BackMatter>(field: K, value: BackMatter[K]) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                backMatter: {
                    ...prev.backMatter,
                    [field]: value,
                },
                isUnsaved: true,
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handlePublishingEssentialsChange = <K extends keyof PublishingEssentials>(field: K, value: PublishingEssentials[K]) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                publishingEssentials: {
                    ...prev.publishingEssentials,
                    [field]: value,
                },
                isUnsaved: true,
            };
        });
        
        // Set blueprint as updated when fields are changed
        if (blueprintState?.setBlueprintUpdated) {
            blueprintState.setBlueprintUpdated(true);
        }
    };

    const handleChaptersChange = (newChapters: Chapter[]) => {
        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                chapters: newChapters,
                isUnsaved: true,
            };
        });
    };

    // Chapter management props
    const handleSelectChapter = (chapterId: string) => {
        setPreventSyncOverride(true);
        
        setProjectState((prev: ProjectState | null) => {
            if (!prev || prev.activeChapterId === chapterId) {
                setPreventSyncOverride(false);
                return prev;
            }
            return {
                ...prev,
                activeChapterId: chapterId,
            };
        });
        
        // Reset the flag after a short delay to allow sync again
        setTimeout(() => {
            setPreventSyncOverride(false);
        }, 100);
    };

    const openSplitChapterModal = (chapter: Chapter) => {
        // Implementation for opening split chapter modal
    };

    const onToggleChapterForMerge = (chapterId: string) => {
        const newSelected = selectedChapterIdsForMerge.includes(chapterId)
            ? selectedChapterIdsForMerge.filter(id => id !== chapterId)
            : [...selectedChapterIdsForMerge, chapterId];
        setSelectedChapterIdsForMerge(newSelected);
        updateMergeableSequenceInfo(newSelected);
    };

    const isChapterSelectionDisabledForMerge = (chapterId: string) => {
        // Implementation for checking if chapter selection is disabled
        return false;
    };

    const updateMergeableSequenceInfo = (selectedIds: string[]) => {
        // Implementation for updating merge sequence info
        if (!projectState || projectState.chapters.length === 0) {
            setMergeableSequenceInfo({ isMergeable: false, selectedChapters: [], firstIndexInProject: 0, lastIndexInProject: 0 });
            return;
        }

        const selected = projectState.chapters
            .filter((chapter: Chapter) => selectedIds.includes(chapter.id))
            .sort((a: Chapter, b: Chapter) => projectState.chapters.indexOf(a) - projectState.chapters.indexOf(b));

        if (selected.length < 2) {
            setMergeableSequenceInfo({ isMergeable: false, selectedChapters: [], firstIndexInProject: 0, lastIndexInProject: 0 });
            return;
        }

        let isContiguous = true;
        for (let i = 0; i < selected.length - 1; i++) {
            const currentChapterIndex = projectState.chapters.indexOf(selected[i]);
            const nextChapterIndex = projectState.chapters.indexOf(selected[i + 1]);
            if (nextChapterIndex !== currentChapterIndex + 1) {
                isContiguous = false;
                break;
            }
        }

        if (isContiguous) {
            setMergeableSequenceInfo({
                isMergeable: true,
                selectedChapters: selected,
                firstIndexInProject: projectState.chapters.indexOf(selected[0]),
                lastIndexInProject: projectState.chapters.indexOf(selected[selected.length - 1])
            });
        } else {
            setMergeableSequenceInfo({ isMergeable: false, selectedChapters: [], firstIndexInProject: 0, lastIndexInProject: 0 });
        }
    };

    const onOpenMergeModal = () => {
        // Implementation for opening merge modal
    };

    // handleDetailInputChange and handleVitalsInputChange now come from useChapterState

    const handleSaveChapterDetails = () => {
        // Implementation for saving chapter details
        if (!activeChapter || !activeChapterDetailEdits || !projectState) return;

        const updatedChapter: Chapter = {
            ...activeChapter,
            title: activeChapterDetailEdits.title || activeChapter.title,
            plotOutline: activeChapterDetailEdits.plotOutline || activeChapter.plotOutline,
            summary: activeChapterDetailEdits.summary || activeChapter.summary,
            vitals: {
                ...activeChapter.vitals,
                ...activeChapterDetailEdits.vitals
            }
        };

        const updatedChapters = projectState.chapters.map((chap: Chapter) =>
            chap.id === updatedChapter.id ? updatedChapter : chap
        );

        setProjectState((prev: ProjectState | null) => {
            if (!prev) return null;
            return {
                ...prev,
                chapters: updatedChapters,
                isUnsaved: true,
            };
        });
        setActiveChapter(updatedChapter);
        // Note: areChapterDetailsCurrentlyUnsaved is now computed from useChapterState
        toast({ title: "Chapter Details Saved", description: "Your changes have been saved locally." });
    };

    const handleGenerateChapterPreview = () => {
        // Implementation for generating chapter preview
    };

    const handleGenerateFullTextFromPreview = () => {
        // Implementation for generating full text from preview
    };

    const handleGenerateFullTextForAllPreviewedChapters = () => {
        // Implementation for generating full text for all previewed chapters
    };

    const handleGenerateShortPreviewForAllChapters = () => {
        // Implementation for generating short preview for all chapters
    };

    const handleReviseText = () => {
        // Implementation for revising text
    };

    const navigateVersion = (direction: 'prev' | 'next') => {
        // Implementation for navigating versions
    };

    const setChapterStatus = (status: Chapter['status']) => {
        // Implementation for setting chapter status
    };

    const onChapterPreviewPercentageChange = (percentage: number) => {
        setChapterPreviewPercentage(percentage);
    };

    const onAddBackMatterListItem = (listField: 'appendices' | 'glossary' | 'bibliography') => {
        // Implementation for adding back matter list item
    };

    const onDeleteBackMatterListItem = (listField: 'appendices' | 'glossary' | 'bibliography', id: string) => {
        // Implementation for deleting back matter list item
    };

    const onUpdateBackMatterListItem = (...args: UpdateBackMatterListItemArgsTuple) => {
        // Implementation for updating back matter list item
    };

    const handleGenerateInitialBlueprint = () => {
        // Implementation for generating initial blueprint
    };

    const handleGenerateChapterOutlines = useCallback(async () => {
        console.log('🔗 [useCoreState] handleGenerateChapterOutlines called with state:', {
            hasProjectState: !!projectState,
            outlineIdea: projectState?.outlineIdea ? `${projectState.outlineIdea.substring(0, 50)}...` : 'null',
            isInitialBlueprintReady,
            projectId: projectState?.projectId,
            hasBlueprint: !!projectState?.blueprint,
            hasBookMetadata: !!projectState?.bookMetadata
        });

        // ✅ FIX: If blueprint is ready, we don't need to validate outline idea since blueprint contains all necessary data
        if (!isInitialBlueprintReady) {
            setError("Please generate your initial blueprint first.");
            return;
        }

        // ✅ FIX: Only check outline idea if blueprint is not ready (for initial generation flow)
        if (!isInitialBlueprintReady && !projectState?.outlineIdea?.trim()) {
            setError("Please enter a book idea first.");
            return;
        }

        if (!projectState) {
            setError("No project state available.");
            return;
        }

        console.log('📊 Current project state before generation:', {
            hasChapters: projectState.chapters?.length || 0,
            projectId: projectState.projectId,
            hasBlueprint: !!projectState.blueprint,
            isInitialBlueprintReady
        });
        setLoadingState('chaptersGen', true);
        setError(null);

        // Build payload for chapter outline generation
        // ✅ FIX: Use blueprint data if outline idea is missing (user has blueprint but empty outline)
        const effectiveOutlineIdea = projectState.outlineIdea?.trim() ||
            projectState.blueprint?.worldBuildingSuggestions?.logLine ||
            projectState.bookMetadata?.title ||
            'Generate chapters based on blueprint data';

        const payload = {
            projectId: projectState.projectId,
            outlineIdea: effectiveOutlineIdea,
            bookMetadata: projectState.bookMetadata,
            worldBuildingData: projectState.blueprint?.worldBuildingSuggestions,
            targetWordCount: getTargetWordCount(dbProject),
        };

        try {
            await generateChapterOutlinesMutation.mutateAsync(payload);
        } catch (error) {
            console.error('❌ Chapter outline generation failed:', error);
            // Error handling is done in the mutation's onError callback
        }
    }, [projectState, isInitialBlueprintReady, dbProject, setLoadingState, setError]);

    const handleRecalculateChapterWordCounts = () => {
        // Implementation for recalculating chapter word counts
    };
    
    // Placeholder function for haveDetailsOrWorldChangedSinceChapterGen
    const haveDetailsOrWorldChangedSinceChapterGen = () => false;
    
    // Placeholder value for detailsOrWorldDirtyButOK
    const detailsOrWorldDirtyButOK = false;

    const confirmOverallDiscardChanges = useCallback((message?: string) => {
        // Synchronous confirmation logic
        const defaultMessage = "You have unsaved changes. Are you sure you want to discard them?";
        return window.confirm(message || defaultMessage);
    }, [projectState?.isUnsaved, projectState?.currentProjectName]);

    return {
        projectState,
        setProjectState,
        isLoading,
        creditBalance,
        requiredCredits,
        activeChapter,
        editor,
        isInitialBlueprintReady,
        dbProject,
        confirmOverallDiscardChanges,
        haveDetailsOrWorldChangedSinceChapterGen,
        detailsOrWorldDirtyButOK,
        apiKey,
        setApiKey,
        openaiApiKey,
        setOpenaiApiKey,
        deepseekApiKey,
        setDeepseekApiKey,
        setLoadingState,
        setError,
        clearError,
        getCoreProjectContextForAI,
        mainView,
        setMainView,
        setChaptersReady,
        setIsInitialBlueprintReady,
        determineInitialViewBasedOnState,
        activeVersion,
        handleProjectStateFieldChange,
        handleTargetWordCountChange,
        handleTargetPageCountChange,
        handleBookMetadataChange,
        handleBlueprintChange,
        handleAuthorDetailsChange,
        handleFrontMatterChange,
        handleBackMatterChange,
        handlePublishingEssentialsChange,
        handleChaptersChange,
        handleSelectChapter,
        openSplitChapterModal,
        onToggleChapterForMerge,
        isChapterSelectionDisabledForMerge,
        mergeableSequenceInfo,
        onOpenMergeModal,
        activeChapterDetailEdits,
        setActiveChapterDetailEdits,
        revisionInput,
        revisionWordCount,
        handleSaveChapterDetails,
        handleGenerateChapterPreview,
        handleGenerateFullTextFromPreview,
        handleGenerateFullTextForAllPreviewedChapters,
        handleGenerateShortPreviewForAllChapters,
        setRevisionInput,
        setRevisionWordCount,
        handleReviseText,
        navigateVersion,
        setChapterStatus,
        chapterPreviewPercentage,
        onChapterPreviewPercentageChange,
        onAddBackMatterListItem,
        onDeleteBackMatterListItem,
        onUpdateBackMatterListItem,
        handleGenerateInitialBlueprint,
        handleGenerateChapterOutlines,
        handleRecalculateChapterWordCounts,
        error,
        handleDetailInputChange: chapterHandleDetailInputChange,
        handleVitalsInputChange: chapterHandleVitalsInputChange,
        areChapterDetailsCurrentlyUnsaved: chapterAreChapterDetailsCurrentlyUnsaved,
    };
};
